import React, { useEffect, useState, useRef } from 'react';
import BackLink from '../../components/common/BackLink';
import Layout from '../../components/common/layout/Layout';
import { callLoading } from '../../store/actions/CommonAction';
import ApiService from '../../services/ApiService';
import moment from 'moment';
import { useDispatch, useSelector } from "react-redux";
import ExcelJS from 'exceljs';
import saveAs from 'file-saver';
import html2canvas from 'html2canvas';
import { toast } from 'react-toastify';
import VerticalLoginBarChart from '../../components/common/charts/VerticalLoginBarChart';

const LoginChart = () => {
    const dispatch = useDispatch();
    const [chartData, setChartData] = useState([]);
    const chartRef = useRef(null);
    const formValue = useSelector(state => state.home.formValue);

    useEffect(() => {
        fetchLoginChartData();
    }, []);

    /**
     * Function to fetch login chart data from the backend and format it for display.
     * It triggers a loading state, sends a request to the backend, and processes the response to display in a chart.
     */
    const fetchLoginChartData = async () => {
        dispatch(callLoading(true)); // Show loading spinner
    
        // Get date range based on selected form values (either custom or fiscal year/quarter)
        const { fromDate, toDate } = ApiService.getDateRangeFromForm(
            formValue.fromDate, formValue.toDate, formValue.financialYears, formValue.quarters
        );
    
        // Adding filter for Web users cause there no data available in database before 8 Aug'24
        const isWebUserDateValid = ApiService.checkDateForWebUsers(fromDate, toDate, formValue.device);
    
        if (isWebUserDateValid && (formValue.device === 'Web' || formValue.device === 'All')) {
            toast.error("Data isn't available for Web Users before 8 Aug'24");
            dispatch(callLoading(false)); // Ensure loading spinner is hidden
            return; // This should stop execution here 
        }
    
        // Format form values with properly formatted dates
        const formValueFormatted = {
            ...formValue,
            fromDate: fromDate ? moment(fromDate).format('YYYY-MM-DD') : '',
            toDate: toDate ? moment(toDate).format('YYYY-MM-DD') : ''
        };
    
        // Send POST request with formatted data
        try {
            const response = await ApiService.postData('report/fetchChartLoginData', formValueFormatted);
            dispatch(callLoading(false)); // Hide loading spinner
    
            if (Array.isArray(response.data)) {
                // Format chart data for display
                const formattedData = response.data.map(item => ({
                    Date: moment(item.Date).format('DD MMM YY'),
                    Web: item.Web,
                    IOS: item.IOS,
                    Android: item.Android
                }));
    
                setChartData(formattedData); // Update chart with formatted data
            } else {
                console.error("Unexpected data format", response.data); // Handle unexpected data format
            }
        } catch (error) {
            dispatch(callLoading(false)); // Hide loading spinner on error
            console.error("Error fetching data: ", error); // Log error
        }
    };
    

    const downloadExcelWithChartImage = async () => {
        // Capture the chart as an image
        const canvas = await html2canvas(chartRef.current);
        const imgData = canvas.toDataURL('image/png');
    
        // Create an Excel file with an image
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Login Data');
    
        // Add a descriptive title in the first row
        worksheet.getCell('A1').value = 'Device Based Login Chart Of Users';
        worksheet.getCell('A1').font = { bold: true, size: 14 };
        worksheet.getCell('A1').alignment = { vertical: 'middle', horizontal: 'center' };
        worksheet.mergeCells('A1:F1');
    
        // Apply light gray color to cells from A1 to F1
        for (let col = 1; col <= 6; col++) {
            worksheet.getCell(1, col).fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFD3D3D3' },
            };
        }

        // Add the image to the Excel worksheet
        const imageId = workbook.addImage({
            base64: imgData,
            extension: 'png',
        });
    
        // Define the range where the image is placed
        worksheet.addImage(imageId, 'C4:S27');
    
        // Apply background color around the chart
        for (let i = 3; i <= 28; i++) {
            for (let j = 2; j <= 20; j++) {
                worksheet.getCell(i, j).fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFFAFAD2' },
                };
            }
        }
    
        // Save the workbook
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/octet-stream' });
        saveAs(blob, 'Login Chart - Buddy.xlsx');
    };
    
    return (
        <Layout sideBarActive={13}>
            <div className="inner_page_body">
                <div className="inner_page_body_green">
                    <div className='container height_full p-0'>
                        <BackLink urlLink={`reports?prevPage=login-chart`} heading={'Back to Reports'} />
                        <div className='inner_page_body_full_white'>
                            <div className='topPart_RCR inner_page_form'>
                                <div className='d-flex justify-content-between align-items-center'>
                                    <h3>Login Chart</h3>
                                    <button 
                                        className="btn btn-primary" 
                                        onClick={downloadExcelWithChartImage}
                                        style={{ marginLeft: '950px' }} // This aligns the button to the right
                                    >
                                        Download Chart
                                    </button>
                                </div>
                            </div>
                            <div className='bottomPart_RCR'>
                                <div ref={chartRef}>
                                    <VerticalLoginBarChart data={chartData} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}

export default LoginChart;
